import { createDocChatTurn } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'

import type { DocChatPreview } from './docChatPreview'
import { createDefaultSearchHistoryItem } from './tabStateUrlHelpers'

export const NEW_AGENTIC_CHAT_TAB_LABEL = 'New chat'

/**
 * A fresh chat tab. With `question`, the first turn is already submitted and
 * streams as soon as the tab mounts, as if typed into the composer.
 */
export const createNewAgenticChatTab = ({
  siteId,
  question,
  openedAt = Date.now(),
}: {
  siteId: string
  question?: string
  openedAt?: number
}): DocumentTab => {
  const searchItem = createDefaultSearchHistoryItem()
  const trimmedQuestion = question?.trim()
  const conversation: DocChatConversation = trimmedQuestion
    ? (() => {
        const turnId = crypto.randomUUID()
        return {
          activeTurnId: turnId,
          turns: [createDocChatTurn({ turnId, query: trimmedQuestion })],
        }
      })()
    : { turns: [] }

  return {
    id: `agentic-chat-${crypto.randomUUID()}`,
    history: [
      {
        ...searchItem,
        searchState: {
          ...searchItem.searchState,
          chatOnly: true,
          searchMode: 'ai',
          query: trimmedQuestion ?? '',
          inputQuery: '',
          docChat: {
            siteId,
            conversation,
          },
        },
      },
    ],
    currentIndex: 0,
    customLabel: trimmedQuestion ?? NEW_AGENTIC_CHAT_TAB_LABEL,
    openedAt,
  }
}

export const createAgenticConversationTab = ({
  composerDraft,
  conversation,
  preview,
  siteId,
  title,
}: {
  composerDraft?: string
  conversation: DocChatConversation
  preview?: DocChatPreview
  siteId: string
  title: string
}): DocumentTab => {
  const searchItem = createDefaultSearchHistoryItem()
  return {
    id: `agentic-conversation-${conversation.id ?? crypto.randomUUID()}`,
    history: [
      {
        ...searchItem,
        searchState: {
          ...searchItem.searchState,
          chatOnly: true,
          searchMode: 'ai',
          // Empty query so reopen does not show the last sent turn.
          query: '',
          inputQuery: composerDraft ?? '',
          docChat: {
            siteId,
            conversation,
            isHydratedHistory: true,
            ...preview,
          },
        },
      },
    ],
    currentIndex: 0,
    customLabel: title,
  }
}
